import { useEffect, useState } from "react";
import { Play, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import RightAdRail from "@/components/RightAdRail";

const SECTIONS = [
  { key: "spoken_english", title: "Spoken English", accent: "bg-[#C7DCF7]" },
  { key: "test_skills", title: "Test Your English Skills", accent: "bg-[#D4EDDB]" },
  { key: "grammar", title: "Grammar Concepts", accent: "bg-[#9F87D8]", text: "text-white" },
  { key: "literature", title: "Literature Chapters", accent: "bg-[#EFE9F7]" },
  { key: "news", title: "Percept News", accent: "bg-[#1F2547]", text: "text-white" },
];

function extractYoutubeId(url = "") {
  // supports watch?v=, youtu.be/, shorts/, /embed/
  const m =
    url.match(/[?&]v=([^&]+)/) ||
    url.match(/youtu\.be\/([^?&]+)/) ||
    url.match(/youtube\.com\/shorts\/([^?&]+)/) ||
    url.match(/youtube\.com\/embed\/([^?&]+)/);
  return m ? m[1] : null;
}

function VideoCard({ v }) {
  const id = extractYoutubeId(v.youtube_url);
  const thumb = id
    ? `https://img.youtube.com/vi/${id}/hqdefault.jpg`
    : "https://images.unsplash.com/photo-1505330622279-bf7d7fc918f4?w=600&q=80";

  function watch() {
    window.open(v.youtube_url, "_blank", "noopener,noreferrer");
  }

  return (
    <article
      data-testid={`video-card-${v.id}`}
      className="group rounded-2xl brutal-shadow overflow-hidden bg-white hover-pop cursor-pointer min-w-[280px] md:min-w-0"
      onClick={watch}
    >
      <div className="relative aspect-video overflow-hidden bg-[#EFE9F7]">
        <img
          src={thumb}
          alt={v.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F2547]/70 to-transparent"></div>
        <button
          data-testid={`video-play-${v.id}`}
          aria-label={`Play ${v.title}`}
          className="absolute inset-0 flex items-center justify-center"
        >
          <span className="w-14 h-14 rounded-full bg-white text-[#9F87D8] brutal-shadow flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="w-5 h-5 fill-[#9F87D8] ml-0.5" />
          </span>
        </button>
        {v.level && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[#1F2547] text-[10px] font-bold uppercase tracking-[0.15em]">
            {v.level}
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-display text-base font-bold tracking-tight text-[#1F2547] leading-snug line-clamp-2">
          {v.title}
        </h3>
      </div>
    </article>
  );
}

function VideoSection({ section, videos }) {
  if (!videos.length) return null;
  const isDark = section.text === "text-white";
  return (
    <section data-testid={`videos-section-${section.key}`} className="mb-12">
      <div
        className={`rounded-3xl ${section.accent} ${section.text || "text-[#1F2547]"} px-6 md:px-8 py-5 mb-6 flex items-center justify-between brutal-shadow-sm`}
      >
        <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">
          {section.title}
        </h2>
        <span
          className={`text-xs font-bold uppercase tracking-[0.2em] ${isDark ? "text-white/75" : "text-[#1F2547]/65"}`}
        >
          {videos.length} video{videos.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {videos.map((v) => (
          <VideoCard key={v.id} v={v} />
        ))}
      </div>
    </section>
  );
}

export default function Videos() {
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  const bySection = SECTIONS.map((s) => ({
    section: s,
    videos: videos
      .filter((v) => v.section === s.key)
      .sort((a, b) => (a.order || 0) - (b.order || 0)),
  }));

  return (
    <main data-testid="videos-page" className="min-h-[60vh]">
      <RightAdRail />
      {/* Hero with intro video placeholder */}
      <section className="bg-[#C7DCF7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F2547]/70">
                / Video Library
              </span>
              <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter mt-4 text-[#1F2547] leading-[0.95]">
                Watch. Mimic. Repeat.
              </h1>
              <p className="mt-5 text-lg text-[#1F2547]/75 font-medium max-w-xl">
                Start with our introductory video, then dive into bite-sized
                lessons across five focused sections.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div
                data-testid="videos-intro-placeholder"
                className="relative w-full aspect-video rounded-3xl brutal-shadow-lg overflow-hidden bg-[#EFE9F7] group cursor-pointer"
              >
                <img
                  src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&q=80"
                  alt="Introductory video"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#1F2547]/45 via-transparent to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    data-testid="videos-intro-play-btn"
                    aria-label="Play introductory video"
                    className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-full brutal-shadow flex items-center justify-center group-hover:scale-110 transition-transform"
                  >
                    <Play className="w-8 h-8 md:w-10 md:h-10 text-[#9F87D8] fill-[#9F87D8] ml-1" />
                  </button>
                </div>
                <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur text-[#1F2547] text-[10px] font-bold uppercase tracking-[0.2em] rounded-full">
                  Introduction
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm font-medium text-[#1F2547]/70">
                <ExternalLink className="w-4 h-4" />
                Placeholder — replace from admin panel anytime.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        {videos.length === 0 ? (
          <div className="text-[#5B6079] font-medium text-center py-12">
            Loading videos…
          </div>
        ) : (
          bySection.map(({ section, videos }) => (
            <VideoSection key={section.key} section={section} videos={videos} />
          ))
        )}
      </section>
    </main>
  );
}
